import { Button, Checkbox, HStack, Input, Stack, Text, VStack } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { saveAuthTokens, signIn } from "../../api";
import LogOutOnly from "../../components/Auth/LogOutOnly";
import { useAuthStore } from "../../store";
import { colors } from "../../styles";

export default function SignIn() {

    /* useAuthStore */

    const { setIsLoggedIn, setIsAdmin, setIsGuest } = useAuthStore();

    /* navigate */

    const navigate = useNavigate();

    /* interface - ISignInForm */

    interface ISignInForm {
        email: string;
        password: string;
    }

    /* useForm */

    const { register, handleSubmit, formState: { errors }, reset, watch, setError } = useForm<ISignInForm>();

    /* state - savedEmail, isEmailSaveChecked */

    const [savedEmail, setSavedEmail] = useState("");
    const [isEmailSaveChecked, setIsEmailSaveChecked] = useState(false);

    /* toggleCheck 함수 */

    const toggleCheck = () => {
        if(isEmailSaveChecked) {
            setIsEmailSaveChecked(false);
        } else {
            setIsEmailSaveChecked(true)
        }
    }

    /* setDefaultValues 함수 */

    const setDefaultValues = async () => {
        const storedEmail = localStorage.getItem('storedEmail');
        console.log("storedEmail:", storedEmail)
        reset({
          email: storedEmail || '',
          password: '',
        });
      };

    useEffect(() => {
        setDefaultValues();
    }, [reset]);

    /* isValidEmail 함수 */
    
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if(email) {
            return emailRegex.test(email);
        }
        return false;
      };
    
    const isEmailFilledRight = isValidEmail(watch("email"));

    ////////////////////////////////////////////////////////////////////// password 유효성 검사

    ////////////////////////////////////////////////////////////////////// 비밀번호 유효성 검사

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&~])[A-Za-z\d@$!%*#?&~]{8,20}$/;
    const isPasswordRegexFilledRight = passwordRegex.test(watch("password"));


    ////////////////////////////////////////////////////////////////////// logInMutation

    const signInMutation = useMutation({
        mutationFn: signIn,
        onSuccess: async (data) => {
            const { server_token, refresh_token } = data;

            if (server_token && refresh_token) {
                await saveAuthTokens(server_token, refresh_token);
            }

            ///////////////////////////////////////////////////////////////////////// 이메일 저장

            if(isEmailSaveChecked) {
                localStorage.setItem("storedEmail", savedEmail);
            } else {
                localStorage.removeItem("storedEmail");
            }

            ///////////////////////////////////////////////////////////////////////// 로그인 상태 저장

            setIsLoggedIn(true);
            setIsAdmin(false);
            setIsGuest(false);

            ///////////////////////////////////////////////////////////////////////// 네비게이트
            
            navigate("/");

        },
        onError: (error) => {
            console.log("error!", error);
        }
    })

    ////////////////////////////////////////////////////////////////////// onSubmit

    const onSubmit = async ({ email, password } : ISignInForm) => {

        if(isEmailSaveChecked) {
            setSavedEmail(email);
        }

        if(!isEmailFilledRight) {
            setError("email", { message: "이메일을 입력해주세요." });
            return;
        }

        if(!isPasswordRegexFilledRight) {
            setError("password", { message: "비밀번호를 입력해주세요." });
            return;
        }

        await signInMutation.mutateAsync({ email, password });
    }

    ////////////////////////////////////////////////////////////////////// 렌더링

    return (
        <LogOutOnly>
            <VStack 
                justifyContent={"center"} 
                alignItems={"center"} 
                padding={10}
                height={"100vh"}
            >
                <Stack 
                    as="form" 
                    onSubmit={handleSubmit(onSubmit)} 
                    width={{
                        base:"100%",
                        sm:"90%",
                        md:"60%",
                        lg:"40%",
                        xl:"40%"
                    }} 
                >

                    {/* 로그인 타이틀 */}

                    <HStack  
                        padding={1} 
                        width={"100%"} 
                        justifyContent={"space-between"}
                    >
                        <Text fontSize={28} fontWeight={700} color={colors.g1}>로그인</Text>
                    </HStack>

                    {/* 이메일 입력 */}

                    <Stack marginTop={5}>
                        <Text fontSize="sm" color={colors.blue}>
                            이메일 주소
                        </Text>
                        <Input 
                            {...register("email", {required:true})} 
                            type="text" 
                            isInvalid={Boolean(errors.email?.message)} 
                            placeholder={"팜챗에 가입한 이메일 입력"} 
                            borderBottom={`1px solid ${colors.g4}`} 
                            borderTop="none" 
                            borderLeft="none" 
                            borderRight="none" 
                            borderRadius="0" 
                            _focus={{
                                borderBottom: `1px solid ${colors.blue}`,
                                boxShadow: "none",
                                outline: "none"
                            }}
                        />
                    </Stack>

                    {/* 비밀번호 입력 */}

                    <Stack marginTop={5}>
                        <Text fontSize="sm" color={colors.blue}>
                            비밀번호
                        </Text>
                        <Input 
                            {...register("password", {required:true})} 
                            type="password" 
                            isInvalid={Boolean(errors.password?.message)} 
                            placeholder={"비밀번호"} 
                            borderBottom={`1px solid ${colors.g4}`}
                            borderTop="none" 
                            borderLeft="none" 
                            borderRight="none" 
                            borderRadius="0"
                            _focus={{
                                borderBottom: `1px solid ${colors.blue}`,
                                boxShadow: "none",
                                outline: "none"
                            }}
                        />
                    </Stack>

                    {/* 아이디 저장 체크박스 */}

                    {signInMutation.isError ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> 아이디 또는 비밀번호가 일치하지 않습니다. </Text>) : null}
                    {errors.email?.message ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> {errors.email?.message} </Text>) : null}
                    {errors.password?.message ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> {errors.password?.message} </Text>) : null}

                    <HStack width={"100%"} justifyContent={"space-between"} marginTop={5} marginBottom={5}>
                        <HStack>
                            <Checkbox colorScheme={"blue"} onChange={toggleCheck} />
                            <Text fontSize={13} fontWeight={500} color={colors.g1}>아이디 저장</Text>
                        </HStack>
                        <Link to={"/find"}>
                            <Text fontSize={13} fontWeight={500} color={colors.link}>아이디/비밀번호 찾기</Text>
                        </Link>
                    </HStack>

                    {/* 로그인 버튼 */}
                    
                    <Button 
                        type="submit" 
                        isLoading={signInMutation.isPending} 
                        size={"md"} 
                        width="100%" 
                        backgroundColor={colors.blue} 
                        color={colors.white} 
                        _hover={{ bg: colors.blue }} 
                        boxShadow="sm"
                        disabled={!isEmailFilledRight || !isPasswordRegexFilledRight}
                        onClick={handleSubmit(onSubmit)}
                    > 
                        로그인 
                    </Button>
                </Stack>
            </VStack>
        </LogOutOnly>
    )
}